<template>
  <div class="library">
    <div class="lib-toolbar">
      <div class="status-tabs" role="tablist">
        <button
          v-for="t in statusTabs"
          :key="t.key"
          type="button"
          role="tab"
          class="status-tab"
          :class="{ active: activeStatusKey === t.key }"
          @click="selectStatus(t)"
        >
          {{ t.label }}
          <em v-if="statusCounts[t.key] != null">{{ statusCounts[t.key] }}</em>
        </button>
      </div>
      <div class="lib-filters">
        <el-input
          v-model="searchQuery"
          class="f-search"
          placeholder="搜索标题"
          clearable
          :prefix-icon="Search"
          @keyup.enter="onSearch"
          @clear="onSearch"
        />
        <el-select v-model="filterPlatform" class="f-platform" placeholder="全部平台" clearable>
          <el-option v-for="(label, key) in platformMap" :key="key" :label="label" :value="key" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          class="f-date"
          type="daterange"
          unlink-panels
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
        />
        <el-select v-model="order" class="f-order">
          <el-option label="最新在前" value="desc" />
          <el-option label="最早在前" value="asc" />
        </el-select>
        <el-button :loading="exportAllLoading" @click="exportAllZip">导出 ZIP</el-button>
      </div>
    </div>

    <transition name="bulk">
      <div v-if="selectedRows.length" class="bulk-bar">
        <span>已选 <b>{{ selectedRows.length }}</b> 篇</span>
        <el-button type="primary" size="small" @click="openPublishDialog(selectedRows)">批量发布</el-button>
        <el-button size="small" @click="askAgent">交给 Agent 处理</el-button>
        <el-button size="small" text @click="clearSelection">取消选择</el-button>
      </div>
    </transition>

    <div class="lib-table mp-panel">
      <el-table
        ref="tableRef"
        :data="articles"
        v-loading="loading"
        row-key="id"
        style="width: 100%"
        @selection-change="onTableSelectionChange"
      >
        <el-table-column type="selection" width="44" reserve-selection />
        <el-table-column label="文章" min-width="360">
          <template #default="{ row }">
            <div class="art-cell" @click="viewArticle(row)">
              <div class="art-title">{{ row.title }}</div>
              <div class="art-meta">
                <span class="pf" :class="`pf-${row.platform}`">{{ platformMap[row.platform] || row.platform }}</span>
                <span class="mono">#{{ row.id }}</span>
                <span v-if="row.article_type">{{ row.article_type }}</span>
                <span v-for="(t, i) in (row.tags || []).slice(0, 3)" :key="i" class="art-tag">#{{ t }}</span>
                <span v-if="(row.tags || []).length > 3" class="art-tag">+{{ row.tags.length - 3 }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="审核" width="120">
          <template #default="{ row }">
            <span class="pill" :class="`review-${row.review_status || 'pending'}`">
              {{ reviewLabel(row.review_status) }}
            </span>
            <span v-if="row.review_score" class="score">{{ Math.round(row.review_score) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="发布" width="100">
          <template #default="{ row }">
            <span class="pill" :class="`publish-${row.publish_status || 'unpublished'}`">
              {{ publishStatusLabel(row.publish_status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="150">
          <template #default="{ row }">
            <span class="time">{{ formatTime(row.created_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="" width="170" fixed="right" align="right">
          <template #default="{ row }">
            <el-button text size="small" type="primary" @click="openPublishDialog([row])">发布</el-button>
            <el-button text size="small" @click="openEditDialog(row)">编辑</el-button>
            <el-dropdown trigger="click" @command="(cmd) => onRowCommand(cmd, row)">
              <el-button text size="small" :icon="MoreFilled" aria-label="更多操作" />
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="view">查看详情</el-dropdown-item>
                  <el-dropdown-item command="export">导出</el-dropdown-item>
                  <el-dropdown-item command="delete" divided class="danger-item">删除</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>
        <template #empty>
          <el-empty :image-size="64" description="没有符合条件的文章">
            <el-button type="primary" plain @click="$router.push('/create')">去创作</el-button>
          </el-empty>
        </template>
      </el-table>

      <div class="lib-footer">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next"
          :page-sizes="[10, 20, 50, 100]"
          @size-change="fetchArticles"
          @current-change="fetchArticles"
        />
      </div>
    </div>

    <!-- 文章详情弹窗 -->
    <el-drawer v-model="dialogVisible" title="文章详情" size="min(760px, 100vw)">
      <div v-if="currentArticle">
        <div class="detail-toolbar">
          <el-button size="small" type="success" @click="openPublishDialog([currentArticle])">发布</el-button>
          <el-button size="small" type="primary" @click="openEditDialog(currentArticle)">编辑</el-button>
          <el-button
            size="small"
            type="warning"
            :loading="rewriting"
            :disabled="!sensitiveHits.length"
            @click="aiRewrite"
          >AI 改写规避敏感词</el-button>
          <el-button size="small" @click="copyArticlePart('title')">复制标题</el-button>
          <el-button size="small" @click="copyArticlePart('tags')">复制标签</el-button>
          <el-button size="small" @click="copyArticlePart('content')">复制正文</el-button>
        </div>
        <h3>{{ currentArticle.title }}</h3>
        <div class="article-tags-block" v-if="currentArticle.tags?.length">
          <span class="tags-label">标签</span>
          <el-tag
            v-for="(t, i) in currentArticle.tags"
            :key="i"
            size="small"
            type="info"
            effect="plain"
            class="tag-chip"
          >{{ t }}</el-tag>
        </div>
        <div class="article-meta">
          <el-tag>{{ platformMap[currentArticle.platform] }}</el-tag>
          <el-tag type="info">{{ currentArticle.article_type }}</el-tag>
          <el-tag :type="reviewTagType(currentArticle.review_status)">
            审查{{ reviewLabel(currentArticle.review_status) }}
          </el-tag>
        </div>
        <div class="review-detail-box">
          <el-alert
            v-if="sensitiveHits.length"
            type="error"
            :closable="false"
            show-icon
            :title="`命中敏感词 ${sensitiveHits.length} 处，正文中已标红，请修改或使用「AI 改写」`"
          >
            <div class="hit-word-chips">
              <el-tag
                v-for="(w, i) in sensitiveWords"
                :key="i"
                size="small"
                type="danger"
                effect="light"
                class="tag-chip"
              >{{ w }}</el-tag>
            </div>
            <div v-if="sensitiveCategories.length" class="hit-cats">
              命中分类：{{ sensitiveCategories.join('、') }}
            </div>
          </el-alert>
          <el-alert
            v-else
            type="success"
            :closable="false"
            show-icon
            title="未命中敏感词，审核通过"
          />
        </div>
        <el-divider />
        <!-- 专栏口播：引用素材卡片区 -->
        <div v-if="referencesOfCurrent.length" class="references-panel">
          <div class="references-head">
            <span class="references-title">引用素材 References</span>
            <el-tag size="small" type="info" effect="plain">共 {{ referencesOfCurrent.length }} 条</el-tag>
            <el-tag
              v-if="parseFailOfCurrent"
              size="small"
              type="warning"
              effect="dark"
              style="margin-left: 6px"
            >⚠️ JSON 解析失败 · 原文已保留</el-tag>
          </div>
          <div class="references-list">
            <div
              v-for="ref in referencesOfCurrent"
              :key="ref.id"
              class="reference-card"
              :id="`ref-${ref.id}`"
            >
              <div class="reference-head">
                <span class="reference-id">[{{ ref.id }}]</span>
                <el-tag size="small" effect="plain" v-if="ref.type">{{ ref.type }}</el-tag>
                <span class="reference-name">{{ ref.name || '—' }}</span>
              </div>
              <div v-if="ref.explanation" class="reference-explain">{{ ref.explanation }}</div>
              <div v-if="ref.quote" class="reference-quote">「 {{ ref.quote }} 」</div>
              <div class="reference-meta">
                <span v-if="ref.source" class="reference-source">来源：{{ ref.source }}</span>
                <a
                  v-if="ref.url && ref.url !== '暂无'"
                  class="reference-url"
                  :href="ref.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ ref.url }}</a>
                <span v-else class="reference-url-empty">链接：暂无</span>
              </div>
            </div>
          </div>
          <el-divider />
        </div>
        <div
          class="article-content"
          v-html="renderArticleContent(currentArticle)"
          @click="onArticleContentClick"
        ></div>
      </div>
    </el-drawer>

    <!-- 编辑文章 -->
    <el-dialog v-model="editDialogVisible" title="编辑文章" width="720px" destroy-on-close>
      <el-form v-if="editForm" label-width="72px">
        <el-form-item label="标题" required>
          <el-input v-model="editForm.title" maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="editForm.tags"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="输入后回车添加标签"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="正文" required>
          <el-input
            v-model="editForm.content"
            type="textarea"
            :rows="18"
            placeholder="支持 Markdown 或纯文本"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingEdit" @click="saveEdit">保存并重新审核</el-button>
      </template>
    </el-dialog>

    <ArticlePublishDialog
      v-model="publishDialogVisible"
      :article-ids="publishArticleIds"
      :article-platforms="publishArticlePlatforms"
      @submitted="onPublishSubmitted"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue'
import { MoreFilled, Search } from '@element-plus/icons-vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import { articleApi } from '../api'
import ArticlePublishDialog from '../components/ArticlePublishDialog.vue'
import { usePageContext } from '../stores/pageContext'
import { useAgentStore } from '../stores/agentStore'
import { formatBeijingDateTime } from '../utils/dateTime'

// 状态页签 = 审核状态 × 发布状态 的常用组合
const STATUS_TABS = [
  { key: 'all', label: '全部', review: '', publish: '' },
  { key: 'review_pending', label: '待审核', review: 'pending', publish: '' },
  { key: 'ready', label: '待发布', review: 'passed', publish: 'unpublished' },
  { key: 'published', label: '已发布', review: '', publish: 'published' },
  { key: 'publish_failed', label: '发布失败', review: '', publish: 'failed' },
  { key: 'review_failed', label: '未过审', review: 'failed', publish: '' },
]

export default {
  name: 'History',
  components: { ArticlePublishDialog },
  setup() {
    const articles = ref([])
    const loading = ref(false)
    const page = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const searchQuery = ref('')
    const filterPlatform = ref('')
    const filterStatus = ref('')
    // 发布状态筛选（与审核状态一起由状态页签设置，也可从工作台跳转带入）
    const filterPublishStatus = ref('')
    const statusCounts = ref({})
    const tableRef = ref(null)
    const agent = useAgentStore()
    const dateRange = ref(null)
    const order = ref('desc')
    const rewriting = ref(false)
    const dialogVisible = ref(false)
    const editDialogVisible = ref(false)
    const editForm = ref(null)
    const savingEdit = ref(false)
    const currentArticle = ref(null)
    const exportAllLoading = ref(false)
    const selectedRows = ref([])
    const publishDialogVisible = ref(false)
    const publishArticleIds = ref([])
    const publishArticlePlatforms = ref([])
    const router = useRouter()

    const platformMap = {
      'xiaohongshu': '小红书',
      'zhihu': '知乎',
      'toutiao': '头条',
      'weixin': '微信公众号',
      'lofter': 'LOFTER',
      'bilibili': 'B站',
      'douyin': '抖音',
      'baijiahao': '百家号',
      'column': '专栏口播'
    }

    const pipelineLabel = (s) => ({
      discovered: '已采集',
      drafted: '草稿',
      generated: '已生成',
      queued: '待发布',
      published: '已发布',
      synced: '已回写',
    }[s] || '已生成')
    const pipelineTagType = (s) => ({
      discovered: 'info',
      drafted: 'warning',
      generated: 'info',
      queued: 'warning',
      published: 'success',
      synced: 'success',
    }[s] || 'info')

    const publishStatusLabel = (s) => {
      const m = {
        unpublished: '未发布',
        pending: '排队中',
        publishing: '发布中',
        published: '已发布',
        draft: '草稿',
        failed: '失败',
      }
      return m[s] || s || '未发布'
    }

    const publishStatusType = (s) => {
      if (s === 'published') return 'success'
      if (s === 'draft') return 'warning'
      if (s === 'failed') return 'danger'
      if (s === 'publishing' || s === 'pending') return 'info'
      return 'info'
    }

    const scoreColor = [
      { color: '#f56c6c', percentage: 60 },
      { color: '#e6a23c', percentage: 80 },
      { color: '#67c23a', percentage: 100 }
    ]

    const listQueryParams = () => {
      const params = {
        skip: (page.value - 1) * pageSize.value,
        limit: pageSize.value,
        order: order.value || 'desc',
      }
      if (searchQuery.value) params.search = searchQuery.value
      if (filterPlatform.value) params.platform = filterPlatform.value
      if (filterStatus.value) params.review_status = filterStatus.value
      if (filterPublishStatus.value) params.publish_status = filterPublishStatus.value
      if (dateRange.value && dateRange.value.length === 2) {
        params.created_from = dateRange.value[0]
        params.created_to = dateRange.value[1]
      }
      return params
    }

    const reviewLabel = (s) => ({ passed: '通过', failed: '未通过', pending: '待审查' }[s] || '待审查')
    const reviewTagType = (s) => ({ passed: 'success', failed: 'danger', pending: 'info' }[s] || 'info')

    const fetchStatusCounts = async () => {
      const params = {}
      if (searchQuery.value) params.search = searchQuery.value
      if (filterPlatform.value) params.platform = filterPlatform.value
      if (dateRange.value && dateRange.value.length === 2) {
        params.created_from = dateRange.value[0]
        params.created_to = dateRange.value[1]
      }
      try {
        const res = await axios.get('/api/articles/status-counts', { params })
        statusCounts.value = res.data || {}
      } catch {
        /* 计数失败不影响列表 */
      }
    }

    const fetchArticles = async () => {
      loading.value = true
      fetchStatusCounts()
      try {
        const res = await axios.get('/api/articles', { params: listQueryParams() })
        articles.value = res.data.items
        total.value = res.data.total
      } catch (e) {
        ElMessage.error('获取文章失败')
      } finally {
        loading.value = false
      }
    }

    const activeStatusKey = computed(() => {
      const hit = STATUS_TABS.find(
        (t) => t.review === (filterStatus.value || '') && t.publish === (filterPublishStatus.value || ''),
      )
      return hit ? hit.key : null
    })

    const selectStatus = (tab) => {
      filterStatus.value = tab.review
      filterPublishStatus.value = tab.publish
    }

    const onSearch = () => {
      page.value = 1
      fetchArticles()
    }

    const clearSelection = () => {
      tableRef.value?.clearSelection()
    }

    const askAgent = () => {
      agent.openPanel()
      ElMessage.info(`已打开 Agent，会附带你选中的 ${selectedRows.value.length} 篇文章`)
    }

    const onRowCommand = (cmd, row) => {
      if (cmd === 'view') viewArticle(row)
      else if (cmd === 'export') exportOne(row)
      else if (cmd === 'delete') deleteArticle(row)
    }

    // 库内时间为 UTC（无时区后缀），统一按北京时间显示
    const formatTime = (iso) => {
      if (!iso) return '—'
      const s = String(iso)
      return formatBeijingDateTime(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`).slice(5)
    }

    const downloadBlob = (blob, filename) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    }

    const exportOne = async (row) => {
      try {
        const res = await axios.get(`/api/articles/${row.id}/export`, { responseType: 'blob' })
        const safe = (row.title || `article_${row.id}`).replace(/[<>:"/\\|?*\n\r]/g, '_').slice(0, 100)
        downloadBlob(res.data, `${safe || row.id}.md`)
        ElMessage.success('已开始下载')
      } catch (e) {
        ElMessage.error('导出失败')
      }
    }

    const exportAllZip = async () => {
      if (total.value === 0) {
        ElMessage.warning('当前没有可导出的文章（可先调整筛选或确认库中有数据）')
        return
      }
      exportAllLoading.value = true
      try {
        const p = new URLSearchParams()
        if (searchQuery.value) p.set('search', searchQuery.value)
        if (filterPlatform.value) p.set('platform', filterPlatform.value)
        if (filterStatus.value) p.set('review_status', filterStatus.value)
        const q = p.toString()
        const res = await axios.get(`/api/articles/export-zip${q ? `?${q}` : ''}`, { responseType: 'blob' })
        const cd = res.headers['content-disposition'] || ''
        let filename = 'articles.zip'
        const m = /filename="([^"]+)"/.exec(cd)
        if (m) filename = m[1]
        downloadBlob(res.data, filename)
        ElMessage.success('已开始下载压缩包（按平台分子目录）')
      } catch (e) {
        if (e.response?.status === 404) {
          ElMessage.warning('没有可导出的文章')
        } else {
          ElMessage.error('导出失败：' + (e.response?.data?.detail || e.message))
        }
      } finally {
        exportAllLoading.value = false
      }
    }

    const viewArticle = async (row) => {
      try {
        const res = await axios.get(`/api/articles/${row.id}`)
        currentArticle.value = res.data
        dialogVisible.value = true
      } catch (e) {
        ElMessage.error('获取文章详情失败')
      }
    }

    const openEditDialog = async (row) => {
      if (!row?.id) return
      try {
        const a = await articleApi.getById(row.id)
        editForm.value = {
          id: a.id,
          title: a.title || '',
          content: a.content || '',
          tags: Array.isArray(a.tags) ? [...a.tags] : [],
        }
        editDialogVisible.value = true
      } catch (e) {
        ElMessage.error('获取文章失败')
      }
    }

    const saveEdit = async () => {
      const form = editForm.value
      if (!form?.id) return
      if (!form.title?.trim()) return ElMessage.warning('请填写标题')
      if (!form.content?.trim()) return ElMessage.warning('请填写正文')
      savingEdit.value = true
      try {
        const updated = await articleApi.update(form.id, {
          title: form.title.trim(),
          content: form.content,
          tags: form.tags || [],
        })
        editDialogVisible.value = false
        if (updated.review_status === 'passed') {
          ElMessage.success('已保存并通过审核')
        } else {
          ElMessage.warning('已保存，但仍有敏感词未通过，请继续修改')
        }
        if (currentArticle.value?.id === form.id) {
          currentArticle.value = await articleApi.getById(form.id)
        }
        fetchArticles()
      } catch (e) {
        ElMessage.error('保存失败：' + (e.response?.data?.detail || e.message))
      } finally {
        savingEdit.value = false
      }
    }

    const deleteArticle = async (row) => {
      try {
        await ElMessageBox.confirm('确定删除这篇文章吗？', '提示', { type: 'warning' })
        await axios.delete(`/api/articles/${row.id}`)
        ElMessage.success('删除成功')
        fetchArticles()
      } catch (e) {
        if (e !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const formatContent = (content) => {
      return (content || '').replace(/\n/g, '<br>')
    }

    // 专栏口播 references 取数（兼容旧版/异常数据）
    const referencesOfCurrent = computed(() => {
      const a = currentArticle.value
      if (!a) return []
      const mu = a.materials_used
      if (mu && typeof mu === 'object' && Array.isArray(mu.references)) {
        return mu.references
      }
      return []
    })
    const parseFailOfCurrent = computed(() => {
      const a = currentArticle.value
      if (!a) return false
      const mu = a.materials_used
      if (mu && typeof mu === 'object' && mu.parse_ok === false) return true
      if (Array.isArray(a.tags) && a.tags.includes('⚠️ JSON 解析失败')) return true
      return false
    })

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

    // 敏感词命中（优先取后端实时检测结果 sensitive，回退 review_details）
    const sensitiveHits = computed(() => {
      const a = currentArticle.value
      if (!a) return []
      if (a.sensitive && Array.isArray(a.sensitive.hits)) return a.sensitive.hits
      const sd = a.review_details?.sensitive || a.review_details?.review?.sensitive
      return sd && Array.isArray(sd.hits) ? sd.hits : []
    })
    const sensitiveWords = computed(() => {
      const a = currentArticle.value
      if (a?.sensitive && Array.isArray(a.sensitive.hit_words)) return a.sensitive.hit_words
      return [...new Set(sensitiveHits.value.map((h) => h.word))]
    })
    const sensitiveCategories = computed(() => {
      const a = currentArticle.value
      if (a?.sensitive && Array.isArray(a.sensitive.categories)) return a.sensitive.categories
      return [...new Set(sensitiveHits.value.map((h) => h.category).filter(Boolean))]
    })

    // 处理非命中文本：转义 + 换行 + [ref:N] 徽章
    const processGap = (text) => {
      const refIds = new Set(referencesOfCurrent.value.map((r) => String(r.id)))
      return escapeHtml(text).replace(/\n/g, '<br>').replace(/\[ref:(\d+)\]/g, (m, n) => {
        const has = refIds.has(String(n))
        const cls = has ? 'ref-badge ref-badge-active' : 'ref-badge ref-badge-missing'
        return `<a href="#ref-${n}" class="${cls}" data-ref-id="${n}">[${n}]</a>`
      })
    }

    // 渲染正文：命中敏感词标红 + [ref:N] 徽章 + 保留换行
    const renderArticleContent = (article) => {
      if (!article) return ''
      const raw = article.content || ''
      const hits = sensitiveHits.value
        .filter((h) => Number.isInteger(h.start) && Number.isInteger(h.end) && h.end > h.start && h.end <= raw.length)
        .slice()
        .sort((a, b) => a.start - b.start)
      let html = ''
      let cursor = 0
      for (const h of hits) {
        if (h.start < cursor) continue // 跳过重叠
        html += processGap(raw.slice(cursor, h.start))
        const cat = escapeHtml(h.category || '')
        html += `<mark class="sensitive-hit" title="${cat}">${escapeHtml(raw.slice(h.start, h.end))}</mark>`
        cursor = h.end
      }
      html += processGap(raw.slice(cursor))
      return html
    }

    const aiRewrite = async () => {
      const a = currentArticle.value
      if (!a) return
      rewriting.value = true
      try {
        const res = await axios.post('/api/review/rewrite', {
          article_id: a.id,
          apply: true,
          max_rounds: 2,
        })
        const detail = await axios.get(`/api/articles/${a.id}`)
        currentArticle.value = detail.data
        if (res.data?.review?.passed) {
          ElMessage.success('已 AI 改写并通过审核')
        } else {
          ElMessage.warning('已改写，但仍有敏感词命中，请人工检查')
        }
        fetchArticles()
      } catch (e) {
        ElMessage.error('AI 改写失败：' + (e.response?.data?.detail || e.message))
      } finally {
        rewriting.value = false
      }
    }

    const onArticleContentClick = (ev) => {
      const el = ev.target
      if (!el || !el.classList || !el.classList.contains('ref-badge')) return
      ev.preventDefault()
      const id = el.getAttribute('data-ref-id')
      if (!id) return
      const card = document.getElementById(`ref-${id}`)
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' })
        card.classList.add('reference-card-flash')
        setTimeout(() => card.classList.remove('reference-card-flash'), 1200)
      }
    }

    const tagsAsPlainText = (article) => {
      const arr = Array.isArray(article?.tags) ? article.tags : []
      return arr.map((t) => String(t).trim()).filter(Boolean).join(' ')
    }

    const copyArticlePart = async (part) => {
      const a = currentArticle.value
      if (!a) return
      let text = ''
      if (part === 'title') text = a.title || ''
      else if (part === 'tags') text = tagsAsPlainText(a)
      else if (part === 'content') text = a.content || ''
      try {
        await navigator.clipboard.writeText(text)
        ElMessage.success(part === 'title' ? '已复制标题' : part === 'tags' ? '已复制标签' : '已复制正文')
      } catch (e) {
        ElMessage.error('复制失败，请检查浏览器权限')
      }
    }

    const reviewDetails = (article) => {
      const raw = article?.review_details || {}
      return raw?.review || raw
    }

    const semanticReview = (article) => {
      return reviewDetails(article)?.semantic_review || {}
    }

    const reviewReason = (article) => {
      if (!article || article.review_status === 'passed') return ''
      const sem = semanticReview(article)
      const reasons = []
      if (sem.truncated) reasons.push(sem.truncation_detail || '正文疑似被截断')
      if (sem.pass_review === false && sem.summary) reasons.push(sem.summary)
      if (sem.huaben_intro === 'none') reasons.push('未识别到话本小说相关介绍')
      const details = reviewDetails(article)
      const platformSuggestions = details?.platform_fit?.suggestions || []
      if (platformSuggestions.length) reasons.push(platformSuggestions[0])
      if (sem.ok === false) reasons.push(`语义审查失败: ${sem.error || '未知错误'}`)
      if (reasons.length === 0) reasons.push('未返回具体审查原因，请查看 review_details 原始字段')
      return reasons.filter(Boolean).join('；')
    }

    const reviewFields = (article) => {
      if (!article) return []
      const sem = semanticReview(article)
      const details = reviewDetails(article)
      const politicalHits = details?.political?.hits || []
      const pornHits = details?.porn?.hits || []
      const violenceHits = details?.violence?.hits || []
      const platformSuggestions = details?.platform_fit?.suggestions || []
      const huabenDeprecated = details?.huaben_safety?.deprecated_features || []
      const huabenNegative = details?.huaben_safety?.negative_patterns || []
      const rows = []
      const reasonText = reviewReason(article)
      if (typeof article.review_score === 'number') rows.push({ label: '审查得分', value: article.review_score })
      rows.push({ label: '涉政命中', value: String(politicalHits.length) })
      rows.push({ label: '涉黄命中', value: String(pornHits.length) })
      rows.push({ label: '黑恶命中', value: String(violenceHits.length) })
      if (article.review_status !== 'passed' && reasonText) rows.push({ label: '审查原因', value: reasonText })
      if (platformSuggestions.length) rows.push({ label: '平台建议', value: platformSuggestions.join('；') })
      if (huabenDeprecated.length) rows.push({ label: '话本下线功能命中', value: huabenDeprecated.join('、') })
      if (huabenNegative.length) rows.push({ label: '话本负面命中', value: huabenNegative.join('、') })
      if (typeof sem.ok !== 'undefined') rows.push({ label: '语义审查执行', value: sem.ok ? '是' : '否' })
      if (typeof sem.truncated !== 'undefined') rows.push({ label: '是否截断', value: sem.truncated ? '是' : '否' })
      if (sem.truncation_detail) rows.push({ label: '截断依据', value: sem.truncation_detail })
      if (sem.huaben_intro) rows.push({ label: '话本触达', value: sem.huaben_intro })
      if (sem.huaben_detail) rows.push({ label: '话本判定说明', value: sem.huaben_detail })
      if (typeof sem.pass_review !== 'undefined') rows.push({ label: '语义判定通过', value: sem.pass_review ? '是' : '否' })
      if (sem.summary) rows.push({ label: '语义结论', value: sem.summary })
      if (typeof sem.ok === 'undefined') rows.push({ label: '语义审查字段', value: '缺失（旧数据或未执行）' })
      return rows
    }

    watch([filterPlatform, filterStatus, filterPublishStatus, dateRange, order], () => {
      page.value = 1
      fetchArticles()
    })

    const onTableSelectionChange = (rows) => {
      selectedRows.value = rows || []
    }

    const openPublishDialog = (rows) => {
      const list = Array.isArray(rows) ? rows : [rows]
      if (!list.length) {
        ElMessage.warning('请先选择文章')
        return
      }
      publishArticleIds.value = list.map((r) => r.id)
      const platforms = [...new Set(list.map((r) => r.platform).filter(Boolean))]
      publishArticlePlatforms.value = platforms
      publishDialogVisible.value = true
    }

    const onPublishSubmitted = () => {
      fetchArticles()
      router.push('/publish?tab=jobs')
    }

    const route = useRoute()

    // 支持 ?open=<articleId> 直达详情（ColumnOral 页、Agent 侧栏「查看」跳转用）
    const openArticleFromQuery = async () => {
      const openId = Number(route.query.open || 0)
      if (openId <= 0) return
      try {
        const res = await axios.get(`/api/articles/${openId}`)
        currentArticle.value = res.data
        dialogVisible.value = true
      } catch (e) {
        // ignore
      }
    }

    onMounted(async () => {
      if (route.query.q) {
        searchQuery.value = String(route.query.q)
      }
      if (route.query.review_status) filterStatus.value = String(route.query.review_status)
      if (route.query.publish_status) filterPublishStatus.value = String(route.query.publish_status)
      await fetchArticles()
      await openArticleFromQuery()
    })
    // 已在本页时再次跳转 ?open= 不会重新挂载
    watch(() => route.query.open, () => openArticleFromQuery())

    // 供侧栏 Agent 读取：当前页文章 + 勾选项 + 打开的详情
    usePageContext(() => ({
      kind: 'article_list',
      description: '内容库文章列表；selected_ids 为用户勾选的文章',
      selected_ids: selectedRows.value.map((r) => r.id),
      items: articles.value.map((a) => ({
        id: a.id,
        title: a.title,
        platform: a.platform,
        review_status: a.review_status,
        publish_status: a.publish_status,
        created_at: a.created_at,
      })),
      open_article_id: dialogVisible.value ? currentArticle.value?.id ?? null : null,
      total: total.value,
      page: page.value,
      filters: {
        search: searchQuery.value || undefined,
        platform: filterPlatform.value || undefined,
        status: filterStatus.value || undefined,
        publish_status: filterPublishStatus.value || undefined,
        date_range: dateRange.value || undefined,
      },
    }))

    return {
      articles,
      loading,
      page,
      pageSize,
      total,
      searchQuery,
      filterPlatform,
      filterStatus,
      filterPublishStatus,
      statusTabs: STATUS_TABS,
      statusCounts,
      activeStatusKey,
      selectStatus,
      onSearch,
      tableRef,
      clearSelection,
      askAgent,
      onRowCommand,
      formatTime,
      MoreFilled,
      Search,
      dateRange,
      order,
      rewriting,
      editDialogVisible,
      editForm,
      savingEdit,
      openEditDialog,
      saveEdit,
      reviewLabel,
      reviewTagType,
      pipelineLabel,
      pipelineTagType,
      sensitiveHits,
      sensitiveWords,
      sensitiveCategories,
      aiRewrite,
      dialogVisible,
      currentArticle,
      exportAllLoading,
      selectedRows,
      onTableSelectionChange,
      openPublishDialog,
      publishDialogVisible,
      publishArticleIds,
      publishArticlePlatforms,
      onPublishSubmitted,
      platformMap,
      publishStatusLabel,
      publishStatusType,
      scoreColor,
      fetchArticles,
      viewArticle,
      deleteArticle,
      exportOne,
      exportAllZip,
      formatContent,
      reviewReason,
      reviewFields,
      copyArticlePart,
      // 专栏口播 references
      referencesOfCurrent,
      parseFailOfCurrent,
      renderArticleContent,
      onArticleContentClick
    }
  }
}
</script>

<style scoped>
.library {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* —— 工具栏：状态页签 + 筛选 —— */
.lib-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
}

.status-tabs {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  border-radius: 9px;
  background: #eef1f6;
}

.status-tab {
  border: 0;
  background: transparent;
  border-radius: 7px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  color: var(--mp-text-secondary);
  cursor: pointer;
  white-space: nowrap;
}

.status-tab em {
  font-style: normal;
  font-size: 11px;
  margin-left: 4px;
  color: var(--mp-text-muted);
}

.status-tab.active {
  background: var(--mp-surface);
  color: var(--mp-accent);
  box-shadow: 0 1px 4px rgba(30, 48, 82, 0.08);
}

.status-tab.active em {
  color: var(--mp-accent);
}

.lib-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.f-search { width: 200px; }
.f-platform { width: 120px; }
.f-date { width: 240px !important; }
.f-order { width: 110px; }

/* —— 批量操作条 —— */
.bulk-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 10px;
  background: var(--mp-ink-deep);
  color: #dfe6f3;
  font-size: 12px;
}

.bulk-bar b {
  color: #fff;
}

.bulk-bar > span {
  margin-right: auto;
}

.bulk-bar .el-button.is-text {
  color: #aab6cc;
}

.bulk-enter-active,
.bulk-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.bulk-enter-from,
.bulk-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* —— 表格 —— */
.lib-table {
  overflow: hidden;
  box-shadow: none;
}

.lib-table :deep(.el-table th.el-table__cell) {
  background: #fafbfd;
  color: var(--mp-text-muted);
  font-size: 12px;
  font-weight: 650;
}

.art-cell {
  cursor: pointer;
  min-width: 0;
  padding: 2px 0;
}

.art-title {
  font-size: 13px;
  font-weight: 650;
  color: var(--mp-text);
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.art-cell:hover .art-title {
  color: var(--mp-accent);
}

.art-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 3px;
  font-size: 11px;
  color: var(--mp-text-muted);
}

.mono {
  font-family: var(--mp-font-mono);
}

.pf {
  padding: 0 6px;
  border-radius: 4px;
  background: #f0f2f6;
  color: var(--mp-text-secondary);
  font-weight: 650;
  line-height: 18px;
}

.pf-xiaohongshu { background: #ffeef0; color: #e0364f; }
.pf-zhihu { background: #eaf2ff; color: #1767e0; }
.pf-toutiao { background: #fff0ee; color: #d83a2b; }
.pf-douyin { background: #eef0f4; color: #20242c; }
.pf-bilibili { background: #ebf7fd; color: #1b9ad6; }
.pf-baijiahao { background: #edf0ff; color: #3953d6; }

.art-tag {
  color: #8d9ab0;
}

.pill {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 650;
  line-height: 18px;
  background: #f0f2f6;
  color: var(--mp-text-secondary);
}

.review-passed,
.publish-published { background: var(--mp-success-soft); color: var(--mp-success); }
.review-failed,
.publish-failed { background: var(--mp-danger-soft); color: #d8482a; }
.review-pending { background: var(--mp-warning-soft); color: #c36b26; }
.publish-pending,
.publish-publishing { background: var(--mp-accent-soft); color: var(--mp-accent); }
.publish-draft { background: #f4eeff; color: #7c4dda; }

.score {
  margin-left: 6px;
  font-family: var(--mp-font-mono);
  font-size: 11px;
  color: var(--mp-text-muted);
}

.time {
  font-family: var(--mp-font-mono);
  font-size: 11px;
  color: var(--mp-text-secondary);
}

:deep(.danger-item) {
  color: var(--el-color-danger);
}

.lib-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid var(--mp-border);
}

@media (max-width: 768px) {
  .lib-filters {
    width: 100%;
  }

  .f-search,
  .f-date {
    flex: 1 1 100%;
    width: 100% !important;
  }

  .status-tabs {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
  }
}

.detail-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.article-tags-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: 8px 0 12px;
}

.tags-label {
  font-size: 13px;
  color: #909399;
  margin-right: 4px;
}

.tag-chip {
  margin: 0 !important;
}

.text-muted {
  color: #c0c4cc;
  font-size: 12px;
}

.article-meta {
  margin: 10px 0;
  display: flex;
  gap: 10px;
}

.article-content {
  line-height: 1.8;
  color: #606266;
}

/* === 专栏口播 · 引用素材区 === */
.references-panel {
  margin-top: 8px;
}
.references-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.references-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}
.references-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
}
.reference-card {
  border: 1px solid #e4e7ed;
  border-left: 3px solid #409eff;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fafbfc;
  transition: background 0.4s ease;
}
.reference-card-flash {
  background: #ecf5ff !important;
}
.reference-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.reference-id {
  color: #409eff;
  font-weight: 600;
  font-size: 13px;
}
.reference-name {
  font-weight: 600;
  color: #303133;
}
.reference-explain {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin-bottom: 6px;
}
.reference-quote {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  padding: 6px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  border-left: 2px solid #c0c4cc;
  margin-bottom: 6px;
}
.reference-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}
.reference-source {
  color: #67c23a;
}
.reference-url {
  color: #409eff;
  word-break: break-all;
  text-decoration: none;
}
.reference-url:hover {
  text-decoration: underline;
}
.reference-url-empty {
  color: #c0c4cc;
}

/* === [ref:N] 徽章 === */
.article-content :deep(.ref-badge),
.ref-badge {
  display: inline-block;
  padding: 0 6px;
  margin: 0 2px;
  font-size: 12px;
  line-height: 18px;
  border-radius: 9px;
  background: #ecf5ff;
  color: #409eff;
  text-decoration: none;
  border: 1px solid #b3d8ff;
  vertical-align: 1px;
}
.article-content :deep(.ref-badge:hover) {
  background: #d9ecff;
}
.article-content :deep(.ref-badge-missing) {
  background: #fdf6ec;
  color: #e6a23c;
  border-color: #f5dab1;
}

.review-detail-box {
  margin-top: 12px;
}

.review-descriptions {
  margin-top: 10px;
}

.hit-word-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.hit-cats {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

/* 正文敏感词标红 */
.article-content :deep(.sensitive-hit) {
  background: #fde2e2;
  color: #c0392b;
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 600;
}
</style>
